/** Helpers for admin payment history rows and detail views. */

export const PAYMENT_HISTORY_STATUSES = ['SUCCESS', 'FAILED'];

const STATUS_LABELS = {
  SUCCESS: 'Successful',
  FAILED: 'Failed',
  PENDING: 'Pending',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
};

/**
 * @param {string | undefined} status
 * @returns {string}
 */
export function humanPaymentStatus(status) {
  if (!status) return '—';
  return STATUS_LABELS[status] || String(status).replace(/_/g, ' ');
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string}
 */
export function paymentRowId(row) {
  return row?.paymentId ?? row?.id ?? '';
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {number | null}
 */
export function pickPaymentAmount(row) {
  if (row?.amount != null) return Number(row.amount);
  return row?.orderDraft?.total ?? null;
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string}
 */
export function pickPaymentCurrency(row) {
  return row?.currency ?? 'INR';
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string}
 */
export function pickMerchantOrderId(row) {
  return row?.merchantOrderId ?? '';
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string}
 */
export function pickPaymentGateway(row) {
  return row?.gateway ? String(row.gateway) : '';
}

/**
 * Timestamp to show in lists (paidAt / failedAt / createdAt by status).
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {unknown}
 */
export function pickPaymentSettledAt(row) {
  if (row?.status === 'SUCCESS') return row?.paidAt ?? row?.createdAt;
  if (row?.status === 'FAILED') return row?.failedAt ?? row?.createdAt;
  return row?.paidAt ?? row?.failedAt ?? row?.createdAt;
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {Record<string, unknown> | null}
 */
export function pickPaymentDraft(row) {
  return row?.orderDraft && typeof row.orderDraft === 'object' ? row.orderDraft : null;
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string}
 */
export function pickPaymentCustomerName(row) {
  const draft = pickPaymentDraft(row);
  return draft?.user?.name ?? '';
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string}
 */
export function pickPaymentCustomerEmail(row) {
  const draft = pickPaymentDraft(row);
  return draft?.user?.email ?? '';
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string}
 */
export function pickPaymentCustomerPhone(row) {
  const draft = pickPaymentDraft(row);
  return draft?.user?.mobile ?? '';
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string}
 */
export function pickPaymentAppUserId(row) {
  return row?.userId ?? pickPaymentDraft(row)?.user?.userId ?? '';
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {unknown[]}
 */
export function pickPaymentLineItems(row) {
  const draft = pickPaymentDraft(row);
  const items = draft?.items;
  return Array.isArray(items) ? items : [];
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {{ label: string, lines: string[], contactNumber: string, name: string } | null}
 */
export function pickPaymentDeliveryAddress(row) {
  const draft = pickPaymentDraft(row);
  const addr = draft?.deliveryAddress;
  if (!addr || typeof addr !== 'object') return null;
  const lines = [addr.addressLine1, addr.addressLine2, [addr.city, addr.zipCode].filter(Boolean).join(' ')].filter(
    Boolean,
  );
  return {
    label: addr.label ?? '',
    lines,
    contactNumber: addr.contactNumber ?? '',
    name: addr.name ?? '',
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {unknown}
 */
export function pickPaymentDeliveryPreference(row) {
  const draft = pickPaymentDraft(row);
  return draft?.deliveryPreference ?? null;
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {Record<string, unknown> | null}
 */
export function pickLinkedOrder(row) {
  return row?.order && typeof row.order === 'object' ? row.order : null;
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {{ invoiceNumber: string, invoiceUrl: string, invoiceGeneratedAt: unknown }}
 */
export function pickInvoiceFromPayment(row) {
  const order = pickLinkedOrder(row);
  if (!order) {
    return { invoiceNumber: '', invoiceUrl: '', invoiceGeneratedAt: null };
  }
  return {
    invoiceNumber: order.invoiceNumber ? String(order.invoiceNumber).trim() : '',
    invoiceUrl: order.invoiceUrl ? String(order.invoiceUrl).trim() : '',
    invoiceGeneratedAt: order.invoiceGeneratedAt ?? null,
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {unknown[]}
 */
export function pickPaymentStatusHistory(row) {
  const hist = row?.statusHistory;
  return Array.isArray(hist) ? hist : [];
}

/**
 * Normalize add-on rows on a payment draft line item.
 * @param {Record<string, unknown> | null | undefined} li
 * @returns {Array<{ name: string, price: number | null, quantity: number, lineTotal: number | null }>}
 */
export function pickLineItemOptions(li) {
  const raw = li?.selectedOptions ?? li?.addons ?? li?.modifiers ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((opt) => {
      if (!opt || typeof opt !== 'object') return null;
      const name = opt.name ?? opt.label ?? 'Option';
      const price = opt.price != null ? Number(opt.price) : null;
      return {
        name: String(name),
        price: price != null && Number.isFinite(price) ? price : null,
        quantity: 1,
        lineTotal: price,
      };
    })
    .filter(Boolean);
}
